import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, Calendar, BookOpen } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language, localize } = useLanguage();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setStatus('error');
      setError(localize({ ar: 'معرف الجلسة غير موجود', en: 'Session ID not found' }));
    }
  }, [sessionId]);

  const verifyPayment = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      // After max attempts, show success anyway (payment likely processed)
      setStatus('success');
      return;
    }

    try {
      // Use the public verify endpoint - NO AUTH REQUIRED
      const response = await axios.get(`${API}/payments/verify/${sessionId}`);
      
      if (response.data.success) {
        setPaymentData(response.data);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (response.data.status === 'expired') {
          setStatus('error');
          setError(localize({ ar: 'انتهت صلاحية جلسة الدفع', en: 'Payment session expired' }));
          return;
        }
      }

      // If not yet paid, poll again
      setTimeout(() => verifyPayment(attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error verifying payment:', err);
      
      if (attempts < maxAttempts - 1) {
        // Retry on error
        setTimeout(() => verifyPayment(attempts + 1), pollInterval);
      } else {
        // Assume success after final attempt (webhook will handle it)
        setStatus('success');
      }
    }
  };

  const handleContinue = () => {
    // Check if user is logged in by checking localStorage
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/student/bookings');
    } else {
      navigate('/login');
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
      <Card className="border-0 shadow-emerald-hover max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'loading' ? (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-6 text-emerald-500 animate-spin" />
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                {localize({ ar: 'جاري التحقق من الدفع...', en: 'Verifying payment...' })}
              </h1>
              <p className="text-slate-500">
                {localize({ ar: 'يرجى الانتظار بينما نتحقق من عملية الدفع', en: 'Please wait while we verify your payment' })}
              </p>
            </>
          ) : status === 'success' ? (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="payment-success-title">
                {localize({ ar: 'تم الدفع بنجاح!', en: 'Payment Successful!' })}
              </h1>
              <p className="text-slate-500 mb-4">
                {localize({ ar: 'شكراً لك! تم تأكيد حجزك وسيتواصل معك الأستاذ قريباً.', en: 'Thank you! Your booking is confirmed and the teacher will contact you soon.' })}
              </p>
              
              {/* Payment Details */}
              {paymentData && (
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-right">
                  {paymentData.subject && (
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-600 flex items-center gap-2">
                        <BookOpen className="w-4 h-4" />
                        {localize({ ar: 'المادة', en: 'Subject' })}
                      </span>
                      <span className="font-medium text-slate-900">{paymentData.subject}</span>
                    </div>
                  )}
                  {paymentData.session_date && (
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-600 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {localize({ ar: 'التاريخ', en: 'Date' })}
                      </span>
                      <span className="font-medium text-slate-900">{paymentData.session_date}</span>
                    </div>
                  )}
                  {paymentData.amount > 0 && (
                    <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                      <span className="text-slate-600">{localize({ ar: 'المبلغ المدفوع', en: 'Amount Paid' })}</span>
                      <span className="font-bold text-green-600">
                        {paymentData.amount} {paymentData.currency?.toUpperCase() || 'AED'}
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-3">
                <Button 
                  onClick={handleContinue}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
                  data-testid="continue-btn"
                >
                  {localize({ ar: 'عرض حجوزاتي', en: 'View My Bookings' })}
                </Button>
                <Button 
                  onClick={handleGoHome}
                  variant="outline"
                  className="w-full rounded-full"
                  data-testid="go-home-btn"
                >
                  {localize({ ar: 'العودة للرئيسية', en: 'Go to Home' })}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="payment-error-title">
                {localize({ ar: 'حدث خطأ', en: 'An Error Occurred' })}
              </h1>
              <p className="text-slate-500 mb-6">
                {error || (localize({ ar: 'لم نتمكن من التحقق من عملية الدفع. يرجى المحاولة مرة أخرى.', en: 'We could not verify the payment. Please try again.' }))}
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => window.location.reload()}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
                  data-testid="retry-btn"
                >
                  {localize({ ar: 'إعادة المحاولة', en: 'Try Again' })}
                </Button>
                <Button 
                  onClick={handleGoHome}
                  variant="outline"
                  className="w-full rounded-full"
                  data-testid="go-home-error-btn"
                >
                  {localize({ ar: 'العودة للرئيسية', en: 'Go to Home' })}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
